import * as XLSX from "xlsx";
import { AppType } from "../app.type";
import { ObsolescenceStatus } from "../../../../shared/models/rm-common-properties";
import { StringUtils } from "../../../../shared/utils/string.utils";
import { CellStyle } from "./excel-cell-style";

/** Text color of the status badges in the app (.badge uses $lightGrey) */
const badgeFontColor = "FFF7F7F7";

/** "Without Data" has no color defined, so it gets a white cell. The badge text color
 * would not be readable on it, so it uses the dark text that app.field.getColor also
 * uses for the light statuses. */
const defaultStatusColor = "FFFFFFFF";
const defaultStatusFontColor = "FF263238";

/** Excel expects colors as "AARRGGBB", without the leading "#" */
function toExcelColor(color: string | undefined) {
  if (color == null || StringUtils.isNullOrEmpty(color)) {
    return "";
  }
  return "FF" + color.replace("#", "").toUpperCase();
}

/** alerts.getColor takes the color of these fields from the category of the alert and
 * not only from the options of the field: for the euRohsStatus category "yes" and "no"
 * are inverted. One color per column cannot show that, so they stay uncolored. */
function isRowDependent(app: AppType, fieldId: string) {
  return (
    fieldId === app.fieldId.alert.newValue ||
    fieldId === app.fieldId.alert.oldValue
  );
}

/** The values of these fields are displayed as colored badges in the app: fields with
 * options where at least one option has a color defined. */
function isFieldToColor(app: AppType, fieldId: string) {
  if (isRowDependent(app, fieldId)) {
    return false;
  }

  try {
    const { options, multiple } = app.field.getFieldSettings(fieldId);

    // the text of a multiple field is a list of option texts, never a single option
    if (options == null || multiple === true) {
      return false;
    }

    return Object.keys(options).some(
      (option) => !StringUtils.isNullOrEmpty(options[option].color)
    );
  } catch (err) {
    return false;
  }
}

/** Map the exported text of every option of a field ("Obsolete", "Active", ...) to the
 * style built from the color of that option */
function getStyleByOptionText(app: AppType, fieldId: string) {
  const styleByText = new Map<string, CellStyle>();

  // withoutData is a filter value, so it is not one of the options of the field
  styleByText.set(app.text.RM.withoutData, {
    background: defaultStatusColor,
    fontColor: defaultStatusFontColor
  });

  // isFieldToColor already made sure that the field has options
  const options = app.field.getFieldSettings(fieldId).options ?? {};

  Object.keys(options).forEach((option) => {
    const { backgroundColor, color } = app.field.getColor(
      fieldId,
      option as ObsolescenceStatus
    );
    const background = toExcelColor(backgroundColor);

    // an option without a color keeps a normal cell, like its badge in the app
    if (background === "") {
      return;
    }

    styleByText.set(app.field.getOptionText(fieldId, option), {
      background,
      fontColor: toExcelColor(color) || badgeFontColor
    });
  });

  return styleByText;
}

/** Give the status cells of the sheet the color of their status, so the export shows the
 * same colors as the badges in the app.
 * `fields` are the exported columns in sheet order, `rowCount` the number of data rows
 * written below the header row. */
export function applyStatusColors(
  app: AppType,
  worksheet: XLSX.WorkSheet,
  fields: string[],
  rowCount: number
) {
  fields.forEach((field, column) => {
    if (!isFieldToColor(app, field)) {
      return;
    }

    const styleByText = getStyleByOptionText(app, field);

    for (let row = 1; row <= rowCount; row++) {
      const cell = worksheet[XLSX.utils.encode_cell({ c: column, r: row })];

      if (cell == null || cell.v == null) {
        continue;
      }

      const style = styleByText.get(String(cell.v));

      if (style != null) {
        cell.s = style;
      }
    }
  });
}
